import React, { useEffect, useState, useContext } from 'react';
import { ApiContext } from '../App';
import Loading from '../components/utils/Loading';
import Breadcrumb from '../components/utils/Breadcrumb';
import ConfirmationDialog from '../components/utils/ConfirmationDialog';
import { toast } from 'react-toastify';
import { Briefcase } from 'lucide-react';
import NewPositionForm from '../components/position/NewPositionForm';

const Offers = () => {
    const API = useContext(ApiContext);
    const [positions, setPositions] = useState([]);
    const [filteredPositions, setFilteredPositions] = useState([]);
    const [filters, setFilters] = useState({ city: '', skill: '', partnerName: '', status: '', priorityStatus: '' });
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [loading, setLoading] = useState(true);
    const [showFilterSidebar, setShowFilterSidebar] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [positionToDelete, setPositionToDelete] = useState(null);
    const [showPositionForm, setShowPositionForm] = useState(false);
    const [activeTab, setActiveTab] = useState('open'); // 'open' or 'closed'
    const [users, setUsers] = useState([]); // Pour la liste des responsables
    const [activeStatusDropdown, setActiveStatusDropdown] = useState(null); // ID de la position avec dropdown actif
    const [activeManagerDropdown, setActiveManagerDropdown] = useState(null); // ID de la position avec dropdown actif
    const [dropdownCoords, setDropdownCoords] = useState({}); // Coordonnées fixes des dropdowns
    

    useEffect(() => {
        const fetchPositions = async () => {
            setLoading(true);
            try {
                const response = await API.get('/api/positions');
                setPositions(response.data);
                setFilteredPositions(response.data);
            } catch (error) {
                console.error('Error fetching positions:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPositions();
    }, [API]);

    // Charger la liste des utilisateurs de la même entreprise pour le dropdown des responsables
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await API.get('/api/user/company');
                console.log('Company users response:', response.data);

                // Le backend retourne directement un tableau, pas un objet avec users
                const usersArray = Array.isArray(response.data) ? response.data : (response.data.users || []);
                console.log('Users array:', usersArray);
                setUsers(usersArray);
            } catch (error) {
                console.error('Error fetching company users:', error);
                // Fallback : essayer de récupérer tous les utilisateurs
                try {
                    const fallbackResponse = await API.get('/api/user/all');
                    console.log('All users fallback response:', fallbackResponse.data);
                    const fallbackUsers = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : (fallbackResponse.data.users || []);
                    setUsers(fallbackUsers);
                } catch (fallbackError) {
                    console.error('Error fetching all users:', fallbackError);
                }
            }
        };
        fetchUsers();
    }, [API]);

    // Fermer les dropdowns de statut et responsable si on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedOnDropdown = event.target.closest('.inline-dropdown-trigger');
            if (!clickedOnDropdown) {
                setActiveStatusDropdown(null);
                setActiveManagerDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Met à jour les filtres et applique le filtrage
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const updatedFilters = { ...filters, [name]: value };

        setFilters(updatedFilters);
        applyFilters(positions, updatedFilters, activeTab);
    };

    // Réinitialiser tous les filtres
    const resetFilters = () => {
        setFilters({ city: '', skill: '', partnerName: '', status: '', priorityStatus: '' });
        applyFilters(positions, { city: '', skill: '', partnerName: '', status: '', priorityStatus: '' }, activeTab);
        setShowFilterSidebar(false);
    };

    // Fonction utilitaire pour appliquer les filtres
    const applyFilters = (positionsList, currentFilters, currentTab) => {
        const filtered = positionsList.filter((position) => {
            const matchesCity = currentFilters.city
                ? position.location?.toLowerCase().includes(currentFilters.city.toLowerCase())
                : true;
            const matchesSkill = currentFilters.skill
                ? position.skills?.some((skill) =>
                      skill.toLowerCase().includes(currentFilters.skill.toLowerCase())
                  )
                : true;
            const matchesPartner = currentFilters.partnerName
                ? position.partner?.name?.toLowerCase().includes(currentFilters.partnerName.toLowerCase())
                : true;
            const matchesStatus = currentFilters.status
                ? currentFilters.status === 'open'
                    ? position.open !== false
                    : position.open === false
                : true;

            // Nouveau filtre par statut de priorité
            const matchesPriorityStatus = currentFilters.priorityStatus
                ? position.status === currentFilters.priorityStatus
                : true;

            // Filtre selon l'onglet actif
            const matchesTab = currentTab === 'open'
                ? position.open !== false
                : position.open === false;

            return matchesCity && matchesSkill && matchesPartner && matchesStatus && matchesPriorityStatus && matchesTab;
        });

        setFilteredPositions(filtered);
    };

    // Gérer le changement d'onglet
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        applyFilters(positions, filters, tab);
    };

    // Effet pour mettre à jour les filtres quand l'onglet change
    useEffect(() => {
        if (positions.length > 0) {
            applyFilters(positions, filters, activeTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, positions]);

    // Gérer le tri
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedPositions = [...filteredPositions].sort((a, b) => {
        if (!sortConfig.key) return 0; // Pas de tri si aucune clé définie

        if (sortConfig.key === 'partner.name') {
            const aValue = a.partner?.name || '';
            const bValue = b.partner?.name || '';
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        } else if (sortConfig.key === 'createdAt') {
            const aDate = new Date(a.createdAt).getTime();
            const bDate = new Date(b.createdAt).getTime();
            return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        } else if (sortConfig.key === 'candidates') {
            const aCount = a.candidates?.length || 0;
            const bCount = b.candidates?.length || 0;
            return sortConfig.direction === 'asc' ? aCount - bCount : bCount - aCount;
        } else {
            const aValue = a[sortConfig.key] || '';
            const bValue = b[sortConfig.key] || '';
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        }
    });

    const handleDeleteRequest = (position) => {
        setPendingDeleteId(position._id);
        setPositionToDelete(position);
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        try {
            await API.delete(`/api/positions/${pendingDeleteId}`);
            setPositions((prevPositions) =>
                prevPositions.filter((position) => position._id !== pendingDeleteId)
            );
            setFilteredPositions((prevFilteredPositions) =>
                prevFilteredPositions.filter((position) => position._id !== pendingDeleteId)
            );
            toast.success('Projet de recrutement supprimé avec succès');
        } catch (error) {
            console.error('Error deleting position:', error);
            toast.error('Erreur lors de la suppression du projet');
        }
    };

    // Calculer depuis combien de temps l'offre est publiée
    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return `${Math.floor(diffDays / 30)} months ago`;
    };

    // Calculer le nombre de candidats dans des étapes d'entretien
    const getInternalCandidatesCount = (position) => {
        if (!position.candidates || !position.steps) return 0;

        return position.candidates.filter(candidate => {
            // Trouver l'étape du candidat
            const candidateStep = position.steps.find(step =>
                step._id === candidate.stageId || step.name === candidate.stage
            );
            // Vérifier si l'étape est l'une des trois étapes d'entretien
            return candidateStep && ["entretien", "entretien 2", "entretien 3"].includes(candidateStep.name);
        }).length;
    };

    // Calculer le nombre de candidats par étape pour une position
    const getCandidatesByStage = (position) => {
        if (!position.candidates || !position.steps) return [];

        const stageCount = {};
        let interviewsCount = 0;

        // Compter les candidats dans chaque étape active
        position.steps
            .filter(step => step.active)
            .sort((a, b) => a.order - b.order)
            .forEach(step => {
                // Grouper les entretiens
                if (step.name.toLowerCase().includes('entretien')) {
                    // Ne pas créer d'entrée individuelle pour les entretiens
                    return;
                }
                stageCount[step.name] = {
                    count: 0,
                    stepId: step._id,
                    order: step.order
                };
            });

        // Compter les candidats
        position.candidates.forEach(candidate => {
            const candidateStep = position.steps.find(step =>
                step._id === candidate.stageId || step.name === candidate.stage
            );
            if (candidateStep) {
                // Si c'est un entretien, ajouter au compteur global d'entretiens
                if (candidateStep.name.toLowerCase().includes('entretien')) {
                    interviewsCount++;
                } else if (stageCount[candidateStep.name]) {
                    stageCount[candidateStep.name].count++;
                }
            }
        });

        // Convertir en tableau
        const stages = Object.entries(stageCount).map(([name, data]) => ({
            name,
            count: data.count,
            order: data.order
        }));

        // Trouver l'index où insérer "Entretiens" (après "CV envoyé")
        const cvEnvoyeIndex = stages.findIndex(stage => stage.name.toLowerCase().includes('cv envoyé'));

        // Ajouter les entretiens groupés à la bonne position
        if (interviewsCount > 0) {
            const insertIndex = cvEnvoyeIndex !== -1 ? cvEnvoyeIndex + 1 : 0;
            stages.splice(insertIndex, 0, {
                name: 'Entretiens',
                count: interviewsCount,
                order: cvEnvoyeIndex !== -1 ? stages[cvEnvoyeIndex].order + 0.5 : -1
            });
        }

        return stages;
    };

    // Mettre à jour le statut d'une position
    const handleStatusUpdate = async (positionId, newStatus) => {
        try {
            await API.put(`/api/positions/${positionId}`, { status: newStatus });

            // Mettre à jour l'état local
            setPositions(prevPositions =>
                prevPositions.map(pos =>
                    pos._id === positionId ? { ...pos, status: newStatus } : pos
                )
            );
            setFilteredPositions(prevFilteredPositions =>
                prevFilteredPositions.map(pos =>
                    pos._id === positionId ? { ...pos, status: newStatus } : pos
                )
            );

            setActiveStatusDropdown(null);
            toast.success('Statut mis à jour');
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Erreur lors de la mise à jour du statut');
        }
    };

    // Mettre à jour le responsable d'une position
    const handleManagerUpdate = async (positionId, managerId) => {
        try {
            const selectedManager = users.find(u => u._id === managerId);

            await API.put(`/api/positions/${positionId}`, {
                manager: managerId || null
            });

            // Mettre à jour l'état local
            setPositions(prevPositions =>
                prevPositions.map(pos =>
                    pos._id === positionId ? { ...pos, manager: selectedManager || null } : pos
                )
            );
            setFilteredPositions(prevFilteredPositions =>
                prevFilteredPositions.map(pos =>
                    pos._id === positionId ? { ...pos, manager: selectedManager || null } : pos
                )
            );

            setActiveManagerDropdown(null);
            toast.success('Responsable mis à jour');
        } catch (error) {
            console.error('Error updating manager:', error);
            toast.error('Erreur lors de la mise à jour du responsable');
        }
    };

    // Calculer la position du dropdown en fonction de l'espace disponible
    const calculateDropdownPosition = (event, dropdownType, positionId) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Hauteur réelle plus précise : 7 options * ~32px par option + padding
        const dropdownHeight = dropdownType === 'status' ? 230 : 240; // Statuts: ~230px, Manager: ~240px

        const spaceBelow = windowHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Si pas assez d'espace en bas mais assez en haut, afficher en haut
        const shouldShowAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

        // Calculer les coordonnées pour position fixed
        const coords = {
            left: rect.left,
            // Espacement réduit : 5px en bas, 5px en haut (collé au bouton)
            top: shouldShowAbove ? rect.top - dropdownHeight - 5 : rect.bottom + 5,
            width: dropdownType === 'status' ? 'auto' : 180  // auto pour status, fixe pour manager
        };

        setDropdownCoords(prev => ({ ...prev, [positionId]: coords }));

        return shouldShowAbove ? 'top' : 'bottom';
    };

    // Liste des statuts disponibles
    const statusOptions = [
        { value: '', label: 'Aucun', color: 'bg-gray-100 text-gray-800' },
        { value: 'prioritaire', label: 'Prioritaire', color: 'bg-red-100 text-red-800' },
        { value: 'important', label: 'Important', color: 'bg-orange-100 text-orange-800' },
        { value: 'moyen', label: 'Moyen', color: 'bg-yellow-100 text-yellow-800' },
        { value: 'pas urgent', label: 'Pas urgent', color: 'bg-blue-100 text-blue-800' },
        { value: 'stand by', label: 'Stand by', color: 'bg-gray-100 text-gray-800' },
        { value: 'recruté', label: 'Recruté', color: 'bg-green-100 text-green-800' }
    ];

    // Afficher le loading pendant le chargement
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Loading message="Chargement..." fullHeight={true}  />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 mx-auto p-4 sm:p-6 lg:p-10 pt-16 ">

            {/* Breadcrumb */}
            <div className='pt-10'>
                <Breadcrumb items={[
                { label: 'Dashboard', href: '/app', icon: 'bi-house' },
                { label: 'Recrutement', href: null, icon: 'bi bi-briefcase', isActive: true }
            ]} />
            </div>

            {/* Header */}
            <div className="bg-white rounded-lg border mb-4">
                <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <Briefcase className="mr-3" size={28} />
                        Recrutement
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Pilotez vos projets de recrutement
                    </p>
                    </div>
                    <div className="flex items-center space-x-3">

                    <button
                        onClick={() => setShowPositionForm(true)}
                        className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700
                        text-white rounded-lg font-medium transition-colors shadow-sm self-start sm:self-auto"
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span className="hidden sm:inline">Nouveau Poste</span>
                        <span className="sm:hidden">Ajouter</span>
                    </button>
                    </div>
                </div>
                </div>
            </div>

            {/* Layout principal avec sidebar intégrée */}
            <div className="flex gap-4">
                {/* Sidebar de filtres intégrée */}
                {showFilterSidebar && (
                    <div className="w-80 flex-shrink-0">
                        <div className="bg-white rounded-lg border p-6 sticky top-24">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6 pb-4 border-b">
                                <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
                                <button
                                    onClick={() => setShowFilterSidebar(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>

                            {/* Filtres */}
                            <div className="space-y-4">
                                {/* Filtre Entreprise */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <i className="bi bi-building mr-2"></i>
                                        Entreprise
                                    </label>
                                    <input
                                        type="text"
                                        name="partnerName"
                                        placeholder="Rechercher..."
                                        value={filters.partnerName}
                                        onChange={handleFilterChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                    />
                                </div>

                                {/* Filtre Localisation */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <i className="bi bi-geo-alt mr-2"></i>
                                        Localisation
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        placeholder="Rechercher..."
                                        value={filters.city}
                                        onChange={handleFilterChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                    />
                                </div>

                                {/* Filtre Compétences */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <i className="bi bi-tools mr-2"></i>
                                        Compétences
                                    </label>
                                    <input
                                        type="text"
                                        name="skill"
                                        placeholder="Rechercher..."
                                        value={filters.skill}
                                        onChange={handleFilterChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                    />
                                </div>

                                {/* Filtre par statut de priorité */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <i className="bi bi-flag mr-2"></i>
                                        Statut de priorité
                                    </label>
                                    <select
                                        name="priorityStatus"
                                        value={filters.priorityStatus}
                                        onChange={handleFilterChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                                    >
                                        <option value="">Tous</option>
                                        <option value="prioritaire">Prioritaire</option>
                                        <option value="important">Important</option>
                                        <option value="moyen">Moyen</option>
                                        <option value="pas urgent">Pas urgent</option>
                                        <option value="stand by">Stand by</option>
                                        <option value="recruté">Recruté</option>
                                    </select>
                                </div>

                                {/* Filtre Statut ouvert/fermé */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <i className="bi bi-circle mr-2"></i>
                                        Statut du poste
                                    </label>
                                    <select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                                    >
                                        <option value="">Tous</option>
                                        <option value="open">Ouverts</option>
                                        <option value="closed">Fermés</option>
                                    </select>
                                </div>
                            </div>

                            {/* Bouton Reset */}
                            {Object.values(filters).some(v => v !== '') && (
                                <button
                                    onClick={resetFilters}
                                    className="w-full mt-6 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm font-medium transition-colors"
                                >
                                    <i className="bi bi-x-circle mr-2"></i>
                                    Effacer tous les filtres
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Contenu principal */}
                <div className="flex-1 min-w-0">

            {/* Onglets Open/Closed */}
            <div className=" rounded-lg  mb-4">
                <div className="flex border-b">
                    <button
                        onClick={() => handleTabChange('open')}
                        className={`  px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'open'
                                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Postes ouverts
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                            {positions.filter(p => p.open !== false).length}
                        </span>
                    </button>
                    <button
                        onClick={() => handleTabChange('closed')}
                        className={` px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'closed'
                                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Postes fermés
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                            {positions.filter(p => p.open === false).length}
                        </span>
                    </button>
                </div>
            </div>

            {/* Menu de filtres et tri */}
            <div className="space-y-4 sm:space-y-6 mb-2">
                {/* Filtres et tri sur la même ligne */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
                    {/* Section Filtres */}
                    <div className="flex items-center gap-3">
                        {/* Bouton principal Filtres */}
                        <button
                            onClick={() => setShowFilterSidebar(!showFilterSidebar)}
                            className={`inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium transition-colors ${
                                showFilterSidebar || Object.values(filters).some(v => v !== '')
                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                                    : 'bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                        >
                            <i className="bi bi-funnel"></i>
                            Filtres
                            {Object.values(filters).filter(v => v !== '').length > 0 && (
                                <span className="bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full">
                                    {Object.values(filters).filter(v => v !== '').length}
                                </span>
                            )}
                        </button>

                        {/* Bouton reset si des filtres sont actifs */}
                        {Object.values(filters).some(v => v !== '') && (
                            <button
                                onClick={resetFilters}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors"
                            >
                                <i className="bi bi-x-circle"></i>
                                Effacer
                            </button>
                        )}
                    </div>

                    {/* Section Tri */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <span className="text-sm font-medium text-slate-600">Trier par :</span>
                        <div className="flex flex-wrap gap-2">
                            <button 
                                onClick={() => handleSort('title')} 
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                    sortConfig.key === 'title' 
                                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                Titre {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </button>
                            <button 
                                onClick={() => handleSort('partner.name')} 
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                    sortConfig.key === 'partner.name' 
                                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                Entreprise {sortConfig.key === 'partner.name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </button>
                            <button 
                                onClick={() => handleSort('createdAt')} 
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                    sortConfig.key === 'createdAt' 
                                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                Date {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Message si aucun résultat */}
            {sortedPositions.length === 0 ? (
                <div className="bg-white rounded-lg p-6 sm:p-8 text-center border">
                    <i className="bi bi-search text-4xl text-gray-400 mb-4"></i>
                    <h3 className="text-lg font-semibold text-gray-700 ">Aucun projet de recrutement</h3>
                    <button
                        onClick={() => setShowPositionForm(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors "
                    >
                        <i className="bi bi-plus-lg"></i>
                        Nouveau Poste
                    </button>
                </div>
            ) : (
                /* Vue en tableau */
                <div className="bg-gray-100 rounded-lg  overflow-hidden">
                    {/* Compteur de résultats */}
                    <div className=" py-3   bg-gray-100">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-700">
                                <span className="font-semibold">{sortedPositions.length}</span> projet(s) de recrutement
                                {sortedPositions.length > 1 ? 's' : ''} trouvé{sortedPositions.length > 1 ? 's' : ''}
                            </p>
                            <p className="text-sm text-gray-500">
                                {sortedPositions.reduce((sum, pos) => sum + (pos.candidates?.length || 0), 0)} candidat
                                {sortedPositions.reduce((sum, pos) => sum + (pos.candidates?.length || 0), 0) > 1 ? 's' : ''} au total
                            </p>
                        </div>
                    </div>

                    {/* Vue Desktop - Tableau */}
                    <div className="hidden md:block border rounded-lg" style={{ overflow: 'visible' }}>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        onClick={() => handleSort('title')}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors rounded-tl-lg"
                                    >
                                        <div className="flex items-center gap-1">
                                            Projet
                                            {sortConfig.key === 'title' && (
                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        onClick={() => handleSort('status')}
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            Statut
                                            {sortConfig.key === 'status' && (
                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        onClick={() => handleSort('manager.name')}
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            Responsable
                                            {sortConfig.key === 'manager.name' && (
                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        onClick={() => handleSort('location')}
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            Localisation
                                            {sortConfig.key === 'location' && (
                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        onClick={() => handleSort('salary')}
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            Package
                                            {sortConfig.key === 'salary' && (
                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                   {/*  <th
                                        onClick={() => handleSort('candidates')}
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            Candidats
                                            {sortConfig.key === 'candidates' && (
                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th> */}
                                    <th
                                        onClick={() => handleSort('createdAt')}
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            Date
                                            {sortConfig.key === 'createdAt' && (
                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tr-lg">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white ">
                                {sortedPositions.map((position, index) => (
                                    <React.Fragment key={position._id}>
                                    <tr
                                        className=" transition-colors cursor-pointer"
                                        onClick={() => window.location.href = `/positions/${position._id}`}
                                    >
                                        <td className={`px-6 pt-4 pb-2 ${index === sortedPositions.length - 1 ? 'rounded-bl-lg' : ''}`}>
                                            <div className="text-base font-medium text-gray-900 hover:text-indigo-700 truncate max-w-md"
                                                title={`${position.partner?.name || 'Non spécifié'} - ${position.title}`}>
                                                <span className="font-semibold">{position.partner?.name || 'Non spécifié'}</span> - {position.title}
                                            </div>
                                        </td>
                                        <td
                                            className="px-4 pt-4 pb-2 relative inline-dropdown-trigger"
                                            style={{ overflow: 'visible' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newActiveId = activeStatusDropdown === position._id ? null : position._id;
                                                setActiveStatusDropdown(newActiveId);
                                                setActiveManagerDropdown(null);

                                                if (newActiveId) {
                                                    calculateDropdownPosition(e, 'status', newActiveId);
                                                }
                                            }}
                                        >
                                            <div className="cursor-pointer hover:bg-gray-50 rounded px-2 py-1 inline-block">
                                                {position.status ? (
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        position.status === 'prioritaire' ? 'bg-red-100 text-red-800' :
                                                        position.status === 'important' ? 'bg-orange-100 text-orange-800' :
                                                        position.status === 'moyen' ? 'bg-yellow-100 text-yellow-800' :
                                                        position.status === 'pas urgent' ? 'bg-blue-100 text-blue-800' :
                                                        position.status === 'stand by' ? 'bg-gray-100 text-gray-800' :
                                                        position.status === 'recruté' ? 'bg-green-100 text-green-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {position.status}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Aucun</span>
                                                )}
                                                <i className="bi bi-chevron-down ml-1 text-gray-400 text-xs"></i>
                                            </div>

                                            {/* Dropdown des statuts */}
                                            {activeStatusDropdown === position._id && dropdownCoords[position._id] && (
                                                <div
                                                    className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] py-1"
                                                    style={{
                                                        left: `${dropdownCoords[position._id].left}px`,
                                                        top: `${dropdownCoords[position._id].top}px`,
                                                        width: 'fit-content'
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {statusOptions.map((option, index) => (
                                                        <button
                                                            key={option.value}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleStatusUpdate(position._id, option.value);
                                                            }}
                                                            className={`block px-2 py-1 hover:bg-gray-50 transition-colors ${
                                                                index === 0 ? 'rounded-t-lg' : ''
                                                            } ${
                                                                index === statusOptions.length - 1 ? 'rounded-b-lg' : ''
                                                            }`}
                                                        >
                                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${option.color}`}>
                                                                {option.label}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td
                                            className="px-4 pt-4 pb-2 relative inline-dropdown-trigger"
                                            style={{ overflow: 'visible' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newActiveId = activeManagerDropdown === position._id ? null : position._id;
                                                setActiveManagerDropdown(newActiveId);
                                                setActiveStatusDropdown(null);

                                                if (newActiveId) {
                                                    calculateDropdownPosition(e, 'manager', newActiveId);
                                                }
                                            }}
                                        >
                                            <div className="cursor-pointer hover:bg-gray-50 rounded px-2 py-1 inline-flex items-center">
                                                {position.manager ? (
                                                    <>
                                                        <i className="bi bi-person-badge mr-1 text-gray-400"></i>
                                                        <span className="truncate max-w-[120px] text-sm">{position.manager.name}</span>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Aucun</span>
                                                )}
                                                <i className="bi bi-chevron-down ml-1 text-gray-400 text-xs"></i>
                                            </div>

                                            {/* Dropdown des responsables */}
                                            {activeManagerDropdown === position._id && dropdownCoords[position._id] && (
                                                <div
                                                    className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto"
                                                    style={{
                                                        left: `${dropdownCoords[position._id].left}px`,
                                                        top: `${dropdownCoords[position._id].top}px`,
                                                        minWidth: `${dropdownCoords[position._id].width}px`
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {/* Option "Aucun" */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleManagerUpdate(position._id, null);
                                                        }}
                                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors text-sm border-b border-gray-100"
                                                    >
                                                        <span className="text-gray-400 italic">Aucun</span>
                                                    </button>

                                                    {/* Liste des utilisateurs */}
                                                    {users.length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-400 italic">
                                                            Aucun utilisateur disponible
                                                        </div>
                                                    ) : (
                                                        users.map((user) => (
                                                            <button
                                                                key={user._id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleManagerUpdate(position._id, user._id);
                                                                }}
                                                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors text-sm ${
                                                                    position.manager?._id === user._id ? 'bg-indigo-50 text-indigo-700' : ''
                                                                }`}
                                                            >
                                                                <div className="flex items-center">
                                                                    <i className="bi bi-person-circle mr-2 text-gray-400"></i>
                                                                    <span className="truncate">{user.name}</span>
                                                                </div>
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 pt-4 pb-2 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <i className="bi bi-geo-alt mr-1 text-gray-400"></i>
                                                {position.location}
                                            </div>
                                        </td>
                                        <td className="px-4 pt-4 pb-2">
                                            <div className="text-xs">
                                                {position.salary ? (
                                                    <div className="flex items-center gap-1">
                                                        <i className="bi bi-cash text-gray-400 flex-shrink-0"></i>
                                                        <span className="font-medium text-gray-900 truncate max-w-[120px]" title={position.salary}>
                                                            {position.salary}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                                {position.bonus && (
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <i className="bi bi-trophy text-green-500 flex-shrink-0"></i>
                                                        <span className="font-medium text-green-700 truncate max-w-[120px]" title={`${position.bonus}€`}>
                                                            {position.bonus}€
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        {/* <td className="px-4 pt-4 pb-2 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <div className="flex items-center">
                                                    <i className="bi bi-people mr-2 text-gray-400"></i>
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        {position.candidates?.length || 0}
                                                    </span>
                                                </div>
                                                {getInternalCandidatesCount(position) > 0 && (
                                                    <div className="flex w-fit items-center mt-1 bg-indigo-600 text-white rounded-full py-1 px-2">
                                                        <MoveRight size={12} className='text-white mr-2'/>
                                                        <span className="text-xs ">
                                                            {getInternalCandidatesCount(position)} en entretien
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td> */}
                                        <td className="px-4 pt-4 pb-2 whitespace-nowrap text-xs text-gray-500">
                                            {getTimeAgo(position.createdAt)}
                                        </td>
                                        <td className={`px-2 pt-4 pb-2 text-center whitespace-nowrap text-sm font-medium ${index === sortedPositions.length - 1 ? 'rounded-br-lg' : ''}`}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteRequest(position);
                                                }}
                                                className="text-red-400 hover:text-red-600 transition-colors"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    </tr>

                                    {/* Ligne : Candidats par étape avec tags */}
                                    <tr
                                        className=" cursor-pointer transition-colors border-b"
                                        onClick={() => window.location.href = `/positions/${position._id}`}
                                    >
                                        <td colSpan="8" className="px-6 py-3">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="text-sm font-medium text-gray-500">Répartition :</span>
                                                {getCandidatesByStage(position)
                                                    .filter(stage => stage.count > 0 && !['à contacter', 'a répondu', 'pas intéressé', 'intéressé'].includes(stage.name.toLowerCase()))
                                                    .map((stage, idx) => {
                                                        const stageLower = stage.name.toLowerCase();
                                                        const isGreen = ['entretiens', 'offre', 'recruté', 'cv envoyé'].includes(stageLower);
                                                        const isRed = stageLower === 'ko';

                                                        return (
                                                            <span
                                                                key={idx}
                                                                className={`inline-flex items-center px-4 py-1.5 rounded-lg text-sm font-medium border-1 ${
                                                                    isRed
                                                                        ? 'bg-red-50 text-red-700 border-red-300'
                                                                        : isGreen
                                                                            ? 'bg-green-50 text-green-700 border-green-300'
                                                                            : 'bg-blue-50 text-blue-700 border-blue-300'
                                                                }`}
                                                            >
                                                                {stage.name}: {stage.count}
                                                            </span>
                                                        );
                                                    })}
                                            </div>
                                        </td>
                                    </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>

                    {/* Vue Mobile - Cards */}
                    <div className="md:hidden space-y-4 bg-gray-100">
                        {sortedPositions.map((position) => (
                            <div
                                key={position._id}
                                className="bg-white border rounded-lg p-4   transition-shadow cursor-pointer"
                                onClick={() => window.location.href = `/positions/${position._id}`}
                            >
                                {/* Header de la card */}
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex-1 min-w-0 pr-2">
                                        <h3 className="text-base font-semibold text-gray-900 mb-1 truncate" title={position.title}>
                                            {position.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 flex items-center truncate" title={position.partner?.name || 'Non spécifié'}>
                                            <i className="bi bi-building mr-1 flex-shrink-0"></i>
                                            <span className="truncate">{position.partner?.name || 'Non spécifié'}</span>
                                        </p>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteRequest(position);
                                        }}
                                        className="text-red-400 hover:text-red-600 transition-colors p-2 flex-shrink-0"
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>

                                {/* Statut */}
                                {position.status && (
                                    <div className="mb-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            position.status === 'prioritaire' ? 'bg-red-100 text-red-800' :
                                            position.status === 'important' ? 'bg-orange-100 text-orange-800' :
                                            position.status === 'moyen' ? 'bg-yellow-100 text-yellow-800' :
                                            position.status === 'pas urgent' ? 'bg-blue-100 text-blue-800' :
                                            position.status === 'stand by' ? 'bg-gray-100 text-gray-800' :
                                            position.status === 'recruté' ? 'bg-green-100 text-green-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {position.status}
                                        </span>
                                    </div>
                                )}

                                {/* Compétences */}
                                {position.skills && position.skills.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-3">
                                        {position.skills.slice(0, 3).map((skill, index) => (
                                            <span key={index} className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                                                {skill}
                                            </span>
                                        ))}
                                        {position.skills.length > 3 && (
                                            <span className="text-xs text-gray-400">+{position.skills.length - 3}</span>
                                        )}
                                    </div>
                                )}

                                {/* Informations principales */}
                                <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                                    {/* Localisation */}
                                    <div className="flex items-center text-gray-700">
                                        <i className="bi bi-geo-alt mr-1 text-gray-400"></i>
                                        <span className="truncate">{position.location}</span>
                                    </div>

                                    {/* Candidats */}
                                    <div className="flex flex-col text-gray-700">
                                        <div className="flex items-center">
                                            <i className="bi bi-people mr-1 text-gray-400"></i>
                                            <span>{position.candidates?.length || 0} candidat{position.candidates?.length > 1 ? 's' : ''}</span>
                                        </div>
                                        {getInternalCandidatesCount(position) > 0 && (
                                            <span className="text-xs text-indigo-600 ml-4 mt-0.5">
                                                {getInternalCandidatesCount(position)} en interne
                                            </span>
                                        )}
                                    </div>

                                    {/* Manager */}
                                    {position.manager && (
                                        <div className="flex items-center text-gray-700 col-span-2">
                                            <i className="bi bi-person-badge mr-1 text-gray-400"></i>
                                            <span className="truncate">{position.manager.name}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Package (Salaire + Prime) */}
                                <div className="flex items-center gap-3 mb-3 text-sm">
                                    {position.salary && (
                                        <div className="flex items-center text-gray-700">
                                            <i className="bi bi-cash mr-1 text-gray-400"></i>
                                            <span className="font-medium">{position.salary}</span>
                                        </div>
                                    )}
                                    {position.bonus && (
                                        <div className="flex items-center text-green-700">
                                            <i className="bi bi-trophy mr-1 text-green-500"></i>
                                            <span className="font-medium">{position.bonus}€</span>
                                        </div>
                                    )}
                                </div>

                                {/* Répartition par étape */}
                                {getCandidatesByStage(position)
                                    .filter(stage => stage.count > 0 && !['à contacter', 'a répondu', 'pas intéressé', 'intéressé'].includes(stage.name.toLowerCase()))
                                    .length > 0 && (
                                    <div className="mb-3 pb-3 border-b border-gray-100">
                                        <p className="text-sm font-medium text-gray-500 mb-2">Répartition :</p>
                                        <div className="flex flex-wrap gap-2">
                                            {getCandidatesByStage(position)
                                                .filter(stage => stage.count > 0 && !['à contacter', 'a répondu', 'pas intéressé', 'intéressé'].includes(stage.name.toLowerCase()))
                                                .map((stage, idx) => {
                                                    const stageLower = stage.name.toLowerCase();
                                                    const isGreen = ['entretiens', 'offre', 'recruté', 'cv envoyé'].includes(stageLower);
                                                    const isRed = stageLower === 'ko';

                                                    return (
                                                        <span
                                                            key={idx}
                                                            className={`inline-flex items-center px-4 py-1.5 rounded-lg text-sm font-medium border-1 ${
                                                                isRed
                                                                    ? 'bg-red-50 text-red-700 border-red-300'
                                                                    : isGreen
                                                                        ? 'bg-green-50 text-green-700 border-green-300'
                                                                        : 'bg-blue-50 text-blue-700 border-blue-300'
                                                            }`}
                                                        >
                                                            {stage.name}: {stage.count}
                                                        </span>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}

                                {/* Footer - Date */}
                                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                    <span className="text-xs text-gray-500">
                                        <i className="bi bi-clock mr-1"></i>
                                        {getTimeAgo(position.createdAt)}
                                    </span>
                                    <i className="bi bi-chevron-right text-gray-400"></i>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

                </div>
                {/* Fin du contenu principal */}
            </div>
            {/* Fin du layout flex */}

            {showPositionForm && (
            <NewPositionForm onClose={() => setShowPositionForm(false)} />
          )}

            {/* Modale de confirmation de suppression */}
            <ConfirmationDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="Confirmer la suppression"
                message={`Êtes-vous sûr de vouloir supprimer le projet de recrutement "${positionToDelete?.title || 'ce poste'}" ${positionToDelete?.partner?.name ? `chez ${positionToDelete.partner.name}` : ''} ? Cette action est irréversible.`}
                confirmText="Supprimer"
                cancelText="Annuler"
                isDangerous={true}
            />
        </div>
    );
};

export default Offers;